import { startOfDay } from 'date-fns';
import * as Yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  CONTACTPHONE_ERROR,
  EMAIL_ERROR,
  EMAIL_REG,
  MIDDLENAME_REG,
  NAME_REG,
  REQUIRED_FIELD_ERROR,
  TELEGRAM_REG,
  TOTAL_INFO_ERROR,
} from '../../utils/constants';
import { hasMinimumReservationDuration } from './functions';

const useValidationSchema = () => {
  const validationSchema = Yup.object().shape({
    rental_object: Yup.number()
      .transform((value, originalValue) => {
        return originalValue ? Number(originalValue) : undefined;
      })
      .required(REQUIRED_FIELD_ERROR),
    status: Yup.string().required(REQUIRED_FIELD_ERROR),
    check_in_date: Yup.date().required(REQUIRED_FIELD_ERROR),
    check_out_date: Yup.date()
      .required(REQUIRED_FIELD_ERROR)
      .test(
        'is-greater',
        'Дата выезда должна быть позже даты заезда',
        function (check_out_date) {
          const { check_in_date } = this.parent;
          return (
            check_in_date && check_out_date && startOfDay(check_out_date) > startOfDay(check_in_date)
          );
        },
      )
      .test(
        'is-less',
        'Бронь не может превышать 180 дней',
        function (check_out_date) {
          const { check_in_date } = this.parent;
          return (
            check_in_date &&
            check_out_date &&
            check_out_date <=
              new Date(check_in_date.getTime() + 180 * 24 * 60 * 60 * 1000)
          );
        },
      ),
    check_in_time: Yup.string()
      .required(REQUIRED_FIELD_ERROR)
      .test(
        'minimum-duration',
        'Бронь должна быть длительностью не менее 12 часов',
        function (check_in_time) {
          const {
            check_in_date,
            check_out_date,
            check_out_time,
          } = this.parent

          return hasMinimumReservationDuration(
            check_in_date,
            check_out_date,
            check_in_time,
            check_out_time,
          );
        },
      ),
    check_out_time: Yup.string()
      .required(REQUIRED_FIELD_ERROR)
      .test(
        'minimum-duration',
        'Бронь должна быть длительностью не менее 12 часов',
        function (check_out_time) {
          const {
            check_in_date,
            check_out_date,
            check_in_time,
          } = this.parent;

          return hasMinimumReservationDuration(
            check_in_date,
            check_out_date,
            check_in_time,
            check_out_time,
          );
        },
      ),
    children: Yup.array(Yup.number()).notRequired(),
    adult: Yup.number()
      .integer()
      .positive()
      .min(1)
      .max(50)
      .required(REQUIRED_FIELD_ERROR),

    has_animals: Yup.boolean().notRequired(),
    animals_count: Yup.string().when('has_animals', {
      is: (has_animals: boolean) => has_animals === true,
      then: (validationSchema) =>
        validationSchema
          .max(2, TOTAL_INFO_ERROR)
          .min(1, REQUIRED_FIELD_ERROR)
          .required(REQUIRED_FIELD_ERROR),
      otherwise: (validationSchema) => validationSchema.notRequired().strip(),
    })
    .test(
        'isNull',
        'Введите положительное число',
        function (animals_count) {
          const { has_animals } = this.parent;
          if (has_animals) return (Number(animals_count)> 0);
          return true
        },
      ),
    animals_costs: Yup.number().transform((value, originalValue) => {
      return originalValue ? Number(originalValue) : undefined;
    }),
    animals_info: Yup.string().when('has_animals', {
      is: (has_animals: boolean) => has_animals === true,
      then: (validationSchema) =>
        validationSchema
          .max(100, TOTAL_INFO_ERROR)
          .required(REQUIRED_FIELD_ERROR),
      otherwise: (validationSchema) => validationSchema.notRequired().strip(),
    }),
    total: Yup.number()
      // .required(REQUIRED_FIELD_ERROR)
      .transform((value, originalValue) => {
        return originalValue ? Number(originalValue) : 0;
      })
      .min(0, 'Сумма не может быть отрицательной'),
    meals_costs: Yup.number()
      .transform((value, originalValue) => {
        return originalValue ? Number(originalValue) : undefined;
      })
      .min(0, 'Сумма не может быть отрицательной'),
    living_costs: Yup.number()
      .required(REQUIRED_FIELD_ERROR)
      .transform((value, originalValue) => {
        return originalValue ? Number(originalValue) : undefined;
      })
      .min(0, 'Сумма не может быть отрицательной'),
    paid: Yup.number()
      .transform((value, originalValue) => {
        return originalValue ? Number(originalValue) : undefined;
      })
      .test(
        'is-less',
        'Сумма оплаты не может превышать общую стоимость',
        function (paid) {
          const { total } = this.parent;
          return (total ? Number(total) : 0) - (paid ? Number(paid) : 0) >= 0;
        },
      ),
    rest: Yup.number()
      .transform((value, originalValue) => {
        return originalValue ? Number(originalValue) : 0;
      })
      .min(0)
      .nullable(),
    owner_comment: Yup.string().trim().notRequired().max(550, TOTAL_INFO_ERROR),
    last_name: Yup.string()
      .max(60, TOTAL_INFO_ERROR)
      .required(REQUIRED_FIELD_ERROR)
      .matches(NAME_REG, TOTAL_INFO_ERROR),
    first_name: Yup.string()
      .max(60, TOTAL_INFO_ERROR)
      .required(REQUIRED_FIELD_ERROR)
      .matches(NAME_REG, TOTAL_INFO_ERROR),
    second_name: Yup.string()
      .max(60, TOTAL_INFO_ERROR)
      .trim()
      .notRequired()
      .matches(MIDDLENAME_REG, TOTAL_INFO_ERROR),
    nationality: Yup.string().required(REQUIRED_FIELD_ERROR),
    // birth_day: Yup.date().notRequired().nullable().max(new Date()),
    email: Yup.string()
      .required(REQUIRED_FIELD_ERROR)
      .email(EMAIL_ERROR)
      .matches(EMAIL_REG, EMAIL_ERROR)
      .max(100, 'Максимальное количество знаков 100'),
    telegram: Yup.string()
      .nullable()
      .notRequired()
      .matches(TELEGRAM_REG, TOTAL_INFO_ERROR)
      .max(32, 'Максимальное количество знаков 32'),
    phone: Yup.string()
      .required(REQUIRED_FIELD_ERROR)
      .test('is-valid', CONTACTPHONE_ERROR, (value) => {
        if (!value) return false;
        return isValidPhoneNumber('+' + value);
      }),
    client_comment: Yup.string()
      .trim()
      .notRequired()
      .max(550, TOTAL_INFO_ERROR),
    _lastSentSnapshot: Yup.string(),
  });
  return validationSchema;
};

export default useValidationSchema;
